// @ts-nocheck

import BackButton from "components/common/BackButton";
import { Button } from "components/common/Button";
import dayjs from "dayjs";
import { Settings, ZoomIn, ZoomOut } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { bugsnagClient } from "../../../bugsnag/bugsnag";
import api from "../../../utils/api";
import ChatBot from "../../common/ChatBot";
import { Message } from "../../common/ChatBot/types";
import { ShortestPathPanel } from "./ShortestPathPanel";
import { useElementCablePath } from "hooks/useElementCablePath";
import { useCablePaths } from "./hooks";
import { drawCablePaths, drawCableRoute } from "./utils";

// Zoom configuration constants
const ZOOM_CONFIG = {
  MIN: 0.01,
  MAX: 3,
  BUTTON_IN_FACTOR: 0.2, // Delta for zoom in button
  BUTTON_OUT_FACTOR: -0.2, // Delta for zoom out button
  WHEEL_SENSITIVITY: 0.2,
  WHEEL_PIXEL_MODE_MULTIPLIER: 0.01,
  WHEEL_LINE_MODE_MULTIPLIER: 0.05,
  WHEEL_MAX_DELTA: 1,
  TRANSITION_DURATION: "0.15s",
};

const STATUSES = {
  installed: "installed",
  uninstalled: "uninstalled",
  real_vs_plan_dev: "real_vs_plan_dev",
  occluded: "occluded",
};

interface IProgressReportViewer {
  sectionData: any;
  projectId: string;
  selectedObjectId?: string | null;
  isViewMode?: boolean;
  autoShowShortestPath?: boolean;
}

const ProgressReportViewer = ({
  sectionData,
  projectId,
  selectedObjectId,
  isViewMode,
  autoShowShortestPath = false,
}: IProgressReportViewer) => {
  const { t } = useTranslation();
  const [detectedObjects, setDetectedObjects] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [planImageLoaded, setPlanImageLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isHoveringObject, setIsHoveringObject] = useState(false);
  const dragStartCoords = useRef({ x: 0, y: 0 });

  // Mobile touch states
  const [isTouching, setIsTouching] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [initialZoom, setInitialZoom] = useState(1);
  const touchStartCoords = useRef({ x: 0, y: 0 });
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [filteredObjectIds, setFilteredObjectIds] = useState([]);

  const { cablePaths, showCablePaths, loadingPaths, toggleCablePaths } =
    useCablePaths(sectionData?.plan?.id || null);

  const {
    cablePathState,
    resetCablePathState,
    resetShortestPath,
    calculateShortestPath,
    fetchCableInfo,
    isCalculatingShortestPath,
  } = useElementCablePath();

  const flashIntervalRef = useRef(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const [shouldFlash, setShouldFlash] = useState(false);

  const canvasRef = useRef(null);
  const planImageRef = useRef(null);
  const containerRef = useRef(null);
  const mountDrawStateRef = useRef("initial");
  const animationFrameRef = useRef(null);

  const selectedObject = useMemo(() => {
    return selectedObjects[selectedObjects.length - 1];
  }, [selectedObjects]);

  const findShortestPath = useCallback(async () => {
    if (selectedObject) {
      await calculateShortestPath({
        floorPlanId: sectionData?.plan?.id,
        floorPlanPaths: cablePaths,
        startNode: {
          ...selectedObject,
          x: selectedObject.position.x,
          y: selectedObject.position.y,
        },
      });
    }
  }, [
    selectedObject,
    calculateShortestPath,
    sectionData?.plan?.id,
    cablePaths,
  ]);

  const zoomToObject = useCallback((obj) => {
    if (!obj || !containerRef.current || !planImageRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();

    // Calculate object center
    const objectCenterX = obj.position.x + 40 / 2;
    const objectCenterY = obj.position.y + 40 / 2;

    // Set zoom level to focus on object (adjust this value as needed)
    const targetZoom = Math.min(1.5, Math.max(0.8, 1.2));

    // Calculate pan offset to center the object in the viewport
    const containerCenterX = containerRect.width / 2;
    const containerCenterY = containerRect.height / 2;

    const newPanX = containerCenterX - objectCenterX * targetZoom;
    const newPanY = containerCenterY - objectCenterY * targetZoom;

    setZoomLevel(targetZoom);
    setPanOffset({ x: newPanX, y: newPanY });
  }, []);

  useEffect(() => {
    if (selectedObjectId && detectedObjects.length > 0) {
      const objId = isNaN(+selectedObjectId)
        ? selectedObjectId
        : +selectedObjectId;
      const _selectedObject = detectedObjects.find((obj) =>
        objId < 0
          ? obj._id === Math.abs(objId) && obj.status === "real_vs_plan_dev"
          : obj._id === objId,
      );
      if (_selectedObject) {
        setSelectedObjects([_selectedObject]);
      }
      if (isViewMode) {
        setFilteredObjectIds([objId]);
      }
    }
  }, [detectedObjects, selectedObjectId, isViewMode]);

  // Auto-fetch cable info if object has installed cable status
  useEffect(() => {
    if (selectedObject?.cableStatus === "installed" && selectedObject?.dwgId) {
      fetchCableInfo(selectedObject.dwgId);
    }
  }, [selectedObject?.cableStatus, selectedObject?.dwgId, fetchCableInfo]);

  // Auto-calculate shortest path if requested via query parameter
  const hasAutoCalculatedPath = useRef(false);
  useEffect(() => {
    if (
      autoShowShortestPath &&
      selectedObject &&
      planImageLoaded &&
      !hasAutoCalculatedPath.current
    ) {
      hasAutoCalculatedPath.current = true;
      findShortestPath();
    }
  }, [autoShowShortestPath, selectedObject, planImageLoaded, findShortestPath]);

  // Auto-zoom to selected object when selectedObjectId is provided
  const isZoomedToObject = useRef(false);
  useEffect(() => {
    if (
      selectedObjectId &&
      selectedObject &&
      planImageLoaded &&
      !autoShowShortestPath &&
      !isZoomedToObject.current
    ) {
      // Add a small delay to ensure the image and canvas are properly rendered
      const timeoutId = setTimeout(() => {
        zoomToObject(selectedObject);
        isZoomedToObject.current = true;
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [
    selectedObject,
    selectedObjectId,
    planImageLoaded,
    autoShowShortestPath,
    zoomToObject,
  ]);

  useEffect(() => {
    const setZoomToFit = () => {
      const container = containerRef.current;
      const img = planImageRef?.current;
      if (container && img) {
        const containerRect = container.getBoundingClientRect();
        const imgNaturalWidth = img.naturalWidth;
        const imgNaturalHeight = img.naturalHeight;
        if (imgNaturalWidth && imgNaturalHeight) {
          const scaleX = containerRect.width / imgNaturalWidth;
          const scaleY = containerRect.height / imgNaturalHeight;
          const fitZoom = Math.min(scaleX, scaleY, 1);
          setZoomLevel(fitZoom);
          setPanOffset({ x: 0, y: 0 });
        }
      }
    };

    const imageElement = planImageRef.current;
    if (imageElement && imageElement.complete) {
      setZoomToFit();
    } else if (imageElement) {
      imageElement.onload = setZoomToFit;
    }
    return () => {
      if (imageElement) {
        imageElement.onload = null;
      }
    };
  }, [containerRef]);

  // Size canvas to container (not image) to avoid huge canvas allocations
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    resizeCanvas();

    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(container);

    return () => observer.disconnect();
  }, [planImageLoaded]);

  const drawObjectsOnCanvas = useCallback(
    ({ visible = true, objects = [], isFlashMode = false }) => {
      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      const performDraw = () => {
        const canvas = canvasRef.current;
        const planImage = planImageRef.current;

        if (!canvas || !planImage) return;

        const ctx = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;

        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (!visible) {
          return;
        }

        // Apply DPR scaling, then pan/zoom to map image-space coords to container-space
        ctx.save();
        ctx.scale(dpr, dpr);
        ctx.translate(panOffset.x, panOffset.y);
        ctx.scale(zoomLevel, zoomLevel);

        objects.forEach((obj) => {
          const x = obj.position.x;
          const y = obj.position.y;
          const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;
          const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;

          const color = `rgb(${obj.dwg_rgb[0]}, ${obj.dwg_rgb[1]}, ${obj.dwg_rgb[2]})`;

          const isSelected = selectedObjects.some(
            (object) => object._id === obj._id,
          );

          ctx.beginPath();
          ctx.arc(x, y, Math.max(width, height) / 2, 0, 2 * Math.PI);
          ctx.fillStyle = color;
          ctx.globalAlpha =
            (isFlashMode || zoomLevel < 0.4) && obj.status !== "uninstalled"
              ? 1
              : 0.2;
          ctx.fill();
          ctx.globalAlpha = 1;

          // Draw border if selected
          if (isSelected) {
            ctx.lineWidth = 3 / zoomLevel;
            ctx.strokeStyle = "#000000"; // black border for selection
            ctx.setLineDash([5, 5]);
            ctx.stroke();
            ctx.setLineDash([]);
          } else if (!(isFlashMode || zoomLevel < 0.4)) {
            ctx.lineWidth = 1;
            ctx.strokeStyle = color; // black border for selection
            ctx.setLineDash([]); // solid border
            ctx.stroke();
          }
        });

        // Draw cable paths
        if (showCablePaths && cablePaths) {
          drawCablePaths(ctx, cablePaths, {
            lineOpacity: 0.7,
            showNodes: false,
            zoomLevel: zoomLevel,
          });
        }

        if (cablePathState.shortestPath?.waypoints.length > 0) {
          drawCableRoute(ctx, cablePathState.shortestPath?.waypoints, {
            color: "#ffff00", // Yellow for calculated route
            opacity: 0.9,
            zoomLevel: zoomLevel,
          });
        }

        // Draw yellow filled circle for source (starting element) if route is selected
        if (
          cablePathState.shortestPath?.waypoints.length > 0 &&
          selectedObject
        ) {
          const x = selectedObject.position.x;
          const y = selectedObject.position.y;
          const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;
          const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;
          const radius = Math.max(width, height) / 2;
          const circleRadius = radius + 8;

          // Draw yellow filled circle
          ctx.beginPath();
          ctx.arc(x, y, circleRadius, 0, 2 * Math.PI);
          ctx.fillStyle = "#eab308";
          ctx.fill();
          ctx.lineWidth = 2 / zoomLevel;
          ctx.strokeStyle = "#ca8a04";
          ctx.stroke();

          // Draw "D" label - size constrained by circle radius with black outline for visibility
          const fontSize = Math.max(
            radius + 8,
            Math.min(circleRadius * 0.6, 36),
          );
          ctx.fillStyle = "#000000";
          ctx.strokeStyle = "transparent";
          ctx.lineWidth = fontSize * 0.15;
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.strokeText("D", x, y);
          ctx.fillText("D", x, y);
        }

        // Draw green filled circle for destination (cable) if route is selected
        if (cablePathState.shortestPath?.waypoints.length > 0) {
          const lastWaypoint =
            cablePathState.shortestPath.waypoints[
              cablePathState.shortestPath.waypoints.length - 1
            ];
          const x = lastWaypoint.x;
          const y = lastWaypoint.y;
          const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;
          const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;
          const radius = Math.max(width, height) / 2;
          const circleRadius = radius + 8;

          // Draw green filled circle
          ctx.beginPath();
          ctx.arc(x, y, circleRadius, 0, 2 * Math.PI);
          ctx.fillStyle = "#22c55e";
          ctx.fill();
          ctx.lineWidth = 2 / zoomLevel;
          ctx.strokeStyle = "#16a34a";
          ctx.stroke();

          // Draw "S" label - size constrained by circle radius with white outline for visibility
          const fontSize = Math.max(
            radius + 8,
            Math.min(circleRadius * 0.6, 36),
          );
          ctx.fillStyle = "#000000";
          ctx.strokeStyle = "transparent";
          ctx.lineWidth = fontSize;
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.strokeText("S", x, y);
          ctx.fillText("S", x, y);
        }

        ctx.restore();
      };

      // Use requestAnimationFrame for smooth rendering
      animationFrameRef.current = requestAnimationFrame(performDraw);
    },
    [
      showCablePaths,
      cablePaths,
      cablePathState.shortestPath?.waypoints,
      selectedObject,
      zoomLevel,
      panOffset,
      selectedObjects,
    ],
  );

  useEffect(() => {
    // Load detected objects for this section
    const loadDetectedObjects = async () => {
      if (!sectionData?._id || !projectId) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await api.fetchDetectedObjectsBySection(
          projectId,
          sectionData._id,
        );

        if (response.success) {
          setDetectedObjects(response.data || []);
          console.log(
            `Loaded ${response.data?.length || 0} detected objects for section ${sectionData._id}`,
          );
        } else {
          throw new Error(
            response.message || "Failed to fetch detected objects",
          );
        }
      } catch (error) {
        console.error("Error loading detected objects:", error);
        setError(error.message || "Failed to load detected objects");
        setDetectedObjects([]);
        bugsnagClient.notify(error);
      } finally {
        setLoading(false);
      }
    };

    loadDetectedObjects();
  }, [sectionData?._id, projectId]);

  const handleClearInProgressDraw = useCallback(() => {
    if (flashIntervalRef.current) {
      clearInterval(flashIntervalRef.current);
      mountDrawStateRef.current = "finished";
      setIsFlashing(false);
    }
  }, []);

  const handleOpenDocumentation = (
    frameReference,
    objectId,
    documentationId,
  ) => {
    const params = new URLSearchParams();
    if (frameReference) {
      params.set("frame", frameReference);
    }
    if (objectId) {
      params.set("object", objectId);
    }
    let url = `/project/${projectId}/sections/${sectionData._id}/documentation/${documentationId}`;
    const paramString = params.toString();
    if (paramString) {
      url += `?${paramString}`;
    }
    window.open(url, "_blank");
  };

  useEffect(() => {
    if (
      planImageLoaded &&
      detectedObjects.length > 0 &&
      (mountDrawStateRef.current === "finished" || isViewMode)
    ) {
      const filteredObjects = detectedObjects
        .filter((obj) => selectedStatuses.includes(obj.status))
        .filter((obj) => selectedTypes.includes(obj.group_id.toString()))
        .filter((obj) => {
          if (filteredObjectIds.length === 0) return true;
          // Deviation elements use negative IDs in filteredObjectIds
          if (obj.status === "real_vs_plan_dev") {
            return filteredObjectIds.includes(-obj._id);
          }
          return filteredObjectIds.includes(obj._id);
        });

      if (shouldFlash && !isViewMode) {
        let visible = true;
        let flashes = 0;
        const maxFlashes = 4;
        setIsFlashing(true);
        drawObjectsOnCanvas({
          visible: true,
          objects: filteredObjects,
          isFlashMode: true,
        });
        flashIntervalRef.current = setInterval(() => {
          visible = !visible;
          drawObjectsOnCanvas({
            visible,
            objects: filteredObjects,
            isFlashMode: true,
          });
          flashes++;
          if (flashes >= maxFlashes) {
            clearInterval(flashIntervalRef.current);
            drawObjectsOnCanvas({
              visible: true,
              objects: filteredObjects,
              isFlashMode: false,
            });
            setIsFlashing(false);
            setShouldFlash(false);
          }
        }, 200);
      } else {
        drawObjectsOnCanvas({ visible: true, objects: filteredObjects });
      }

      return () => {
        if (flashIntervalRef.current) {
          clearInterval(flashIntervalRef.current);
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }
  }, [
    detectedObjects,
    planImageLoaded,
    selectedStatuses,
    selectedTypes,
    filteredObjectIds,
    shouldFlash,
    drawObjectsOnCanvas,
    isViewMode,
  ]);

  // Add flash effect on mount also
  useEffect(() => {
    if (isViewMode) return;
    if (planImageLoaded && detectedObjects.length > 0) {
      if (mountDrawStateRef.current === "finished") return;
      mountDrawStateRef.current = "in_progress";
      if (flashIntervalRef.current) {
        clearInterval(flashIntervalRef.current);
      }
      let visible = true;
      let flashes = 0;
      const maxFlashes = 6;
      setIsFlashing(true);
      drawObjectsOnCanvas({
        visible: true,
        objects: detectedObjects,
        isFlashMode: true,
      });
      flashIntervalRef.current = setInterval(() => {
        visible = !visible;
        drawObjectsOnCanvas({
          visible,
          objects: detectedObjects,
          isFlashMode: true,
        });
        flashes++;
        if (flashes >= maxFlashes) {
          clearInterval(flashIntervalRef.current);
          drawObjectsOnCanvas({
            visible: true,
            objects: detectedObjects,
            isFlashMode: false,
          });
          setIsFlashing(false);
          mountDrawStateRef.current = "finished";
        }
      }, 200);
    }
    return () => {
      if (flashIntervalRef.current) {
        clearInterval(flashIntervalRef.current);
      }
    };
  }, [
    detectedObjects,
    selectedObjects,
    isViewMode,
    planImageLoaded,
    drawObjectsOnCanvas,
  ]);

  const handleObjectClick = useCallback(
    (event) => {
      if (isFlashing) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      let clickX = event.clientX - containerRect.left;
      let clickY = event.clientY - containerRect.top;

      // Adjust click coordinates for canvas transforms (pan and zoom)
      clickX = (clickX - panOffset.x) / zoomLevel;
      clickY = (clickY - panOffset.y) / zoomLevel;

      // Find clicked object
      const clickedObject = detectedObjects.find((obj) => {
        const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;
        const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 2) : 40;

        const x = obj.position.x - width / 2;
        const y = obj.position.y - height / 2;

        const isInBounds =
          clickX >= x &&
          clickX <= x + width &&
          clickY >= y &&
          clickY <= y + height;

        return isInBounds;
      });

      if (clickedObject) {
        setSelectedObjects([clickedObject]);
        resetCablePathState(); // Clear route when selecting new object
      } else {
        setSelectedObjects([]);
        resetCablePathState();
      }
    },
    [
      isFlashing,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      detectedObjects,
      resetCablePathState,
    ],
  );

  const handleZoom = useCallback(
    (directionOrEvent, mousePosition = null) => {
      let delta;
      let zoomPoint = mousePosition;

      // Handle different input types
      if (typeof directionOrEvent === "string") {
        // Button click - simple direction string
        delta =
          directionOrEvent === "in"
            ? ZOOM_CONFIG.BUTTON_IN_FACTOR
            : ZOOM_CONFIG.BUTTON_OUT_FACTOR;
      } else if (directionOrEvent && directionOrEvent.deltaY !== undefined) {
        // Wheel event - normalize delta
        const event = directionOrEvent;
        delta = event.deltaY;

        // Handle different deltaMode values
        if (event.deltaMode === 0) {
          // Pixel mode (most common)
          delta *= ZOOM_CONFIG.WHEEL_PIXEL_MODE_MULTIPLIER;
        } else if (event.deltaMode === 1) {
          // Line mode
          delta *= ZOOM_CONFIG.WHEEL_LINE_MODE_MULTIPLIER;
        }

        // Clamp delta to prevent extreme zoom jumps
        delta = Math.max(
          -ZOOM_CONFIG.WHEEL_MAX_DELTA,
          Math.min(ZOOM_CONFIG.WHEEL_MAX_DELTA, delta),
        );

        // Negate for natural zoom direction (scroll down = zoom out)
        delta = -delta;

        // Use event position if not provided
        if (!zoomPoint) {
          zoomPoint = {
            x: event.clientX,
            y: event.clientY,
          };
        }
      } else {
        console.warn("Invalid zoom input");
        return;
      }

      // Calculate exponential zoom factor
      const factor = Math.exp(delta * ZOOM_CONFIG.WHEEL_SENSITIVITY);
      const newZoom = zoomLevel * factor;
      const clampedZoom = Math.max(
        ZOOM_CONFIG.MIN,
        Math.min(ZOOM_CONFIG.MAX, newZoom),
      );

      // Calculate pan offset to maintain zoom point position
      const containerRect = containerRef.current.getBoundingClientRect();
      const mx = zoomPoint
        ? zoomPoint.x - containerRect.left
        : containerRect.width / 2;
      const my = zoomPoint
        ? zoomPoint.y - containerRect.top
        : containerRect.height / 2;

      const imageX = (mx - panOffset.x) / zoomLevel;
      const imageY = (my - panOffset.y) / zoomLevel;

      const newPanX = mx - imageX * clampedZoom;
      const newPanY = my - imageY * clampedZoom;

      setZoomLevel(clampedZoom);
      setPanOffset({ x: newPanX, y: newPanY });
    },
    [zoomLevel, panOffset],
  );

  const handleContainerMouseMove = (event) => {
    if (isDragging) {
      return;
    }

    if (!planImageLoaded) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    let moveX = event.clientX - containerRect.left;
    let moveY = event.clientY - containerRect.top;

    moveX = (moveX - panOffset.x) / zoomLevel;
    moveY = (moveY - panOffset.y) / zoomLevel;

    const isOverObject = detectedObjects.some((obj) => {
      const x = obj.position.x;
      const y = obj.position.y;
      const width = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 3) : 40;
      const height = zoomLevel < 1 ? Math.min(40 / zoomLevel, 40 * 3) : 40;

      return (
        moveX >= x && moveX <= x + width && moveY >= y && moveY <= y + height
      );
    });

    if (isOverObject !== isHoveringObject) {
      setIsHoveringObject(isOverObject);
    }
  };

  const handleMouseDown = (event) => {
    // Handle both mouse clicks and trackpad clicks
    if (event.button === 0) {
      // Left mouse button or trackpad click
      setIsDragging(true);
      setDragStart({
        x: event.clientX - panOffset.x,
        y: event.clientY - panOffset.y,
      });
      dragStartCoords.current = { x: event.clientX, y: event.clientY };
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleMouseMove = useCallback(
    (event) => {
      if (isDragging) {
        const newPanOffset = {
          x: event.clientX - dragStart.x,
          y: event.clientY - dragStart.y,
        };
        setPanOffset(newPanOffset);
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isDragging, dragStart],
  );

  const handleMouseUp = useCallback(
    (event) => {
      if (isDragging) {
        setIsDragging(false);

        const dx = Math.abs(event.clientX - dragStartCoords.current.x);
        const dy = Math.abs(event.clientY - dragStartCoords.current.y);

        if (dx < 5 && dy < 5) {
          handleObjectClick(event);
        }

        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isDragging, handleObjectClick],
  );

  const handleMouseLeave = () => {
    // Stop dragging if mouse leaves the container
    setIsDragging(false);
  };

  // Mobile touch event handlers
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touches) => {
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    const x = (touches[0].clientX + touches[1].clientX) / 2;
    const y = (touches[0].clientY + touches[1].clientY) / 2;
    return { x, y };
  };

  const handleTouchStart = (event) => {
    event.preventDefault();
    if (event.touches.length === 1) {
      // Single finger - start panning
      setIsTouching(true);
      const touch = event.touches[0];
      setTouchStart({
        x: touch.clientX - panOffset.x,
        y: touch.clientY - panOffset.y,
      });
      touchStartCoords.current = { x: touch.clientX, y: touch.clientY };
    } else if (event.touches.length === 2) {
      // Two fingers - start pinch zoom
      setIsTouching(false); // Stop panning
      const distance = getTouchDistance(event.touches);
      setInitialPinchDistance(distance);
      setInitialZoom(zoomLevel);
    }
  };

  const handleTouchMove = useCallback(
    (event) => {
      event.preventDefault();
      if (event.touches.length === 1 && isTouching) {
        // Single finger - pan
        const touch = event.touches[0];
        const newPanOffset = {
          x: touch.clientX - touchStart.x,
          y: touch.clientY - touchStart.y,
        };
        setPanOffset(newPanOffset);
      } else if (event.touches.length === 2) {
        // Two fingers - pinch zoom
        const distance = getTouchDistance(event.touches);
        const center = getTouchCenter(event.touches);

        if (initialPinchDistance > 0) {
          const scale = distance / initialPinchDistance;
          const newZoom = Math.max(0.1, Math.min(5, initialZoom * scale));

          // Calculate zoom with center point
          const containerRect = containerRef.current?.getBoundingClientRect();
          if (containerRect) {
            const mousePosition = {
              x: center.x - containerRect.left,
              y: center.y - containerRect.top,
            };

            // Apply zoom centered on pinch point
            const zoomFactor = newZoom / zoomLevel;
            const newPanX =
              mousePosition.x - (mousePosition.x - panOffset.x) * zoomFactor;
            const newPanY =
              mousePosition.y - (mousePosition.y - panOffset.y) * zoomFactor;

            setZoomLevel(newZoom);
            setPanOffset({ x: newPanX, y: newPanY });
          }
        }
      }
    },
    [
      isTouching,
      touchStart,
      initialPinchDistance,
      initialZoom,
      zoomLevel,
      panOffset,
    ],
  );

  const handleTouchEnd = useCallback(
    (event) => {
      if (event.touches.length === 0) {
        // All fingers lifted
        if (isTouching) {
          setIsTouching(false);

          // Check if it was a tap (minimal movement)
          const touch = event.changedTouches[0];
          const dx = Math.abs(touch.clientX - touchStartCoords.current.x);
          const dy = Math.abs(touch.clientY - touchStartCoords.current.y);

          if (dx < 10 && dy < 10) {
            // Simulate click for object selection
            const syntheticEvent = {
              clientX: touch.clientX,
              clientY: touch.clientY,
              preventDefault: () => {},
              stopPropagation: () => {},
            };
            handleObjectClick(syntheticEvent);
          }
        }

        // Reset pinch zoom state
        setInitialPinchDistance(0);
        setInitialZoom(1);
      } else if (event.touches.length === 1) {
        // One finger remaining, restart single touch
        setInitialPinchDistance(0);
        if (!isTouching) {
          const touch = event.touches[0];
          setIsTouching(true);
          setTouchStart({
            x: touch.clientX - panOffset.x,
            y: touch.clientY - panOffset.y,
          });
          touchStartCoords.current = { x: touch.clientX, y: touch.clientY };
        }
      }
    },
    [isTouching, handleObjectClick, panOffset],
  );

  // Handle wheel zoom
  const handleWheel = useCallback(
    (event) => {
      event.preventDefault();
      handleZoom(event);
    },
    [handleZoom],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });

      return () => {
        container.removeEventListener("wheel", handleWheel);
      };
    }
  }, [handleWheel]);

  // Add global mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStart]);

  // Add global touch event listeners for touch interactions
  useEffect(() => {
    if (isTouching) {
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, { passive: false });

      return () => {
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTouching, touchStart]);

  const getObjectStats = () => {
    const total = detectedObjects.length;
    const installed = detectedObjects.filter(
      (obj) => obj.status === "installed",
    ).length;
    const uninstalled = detectedObjects.filter(
      (obj) => obj.status === "uninstalled",
    ).length;
    const plan_vs_real_dev = detectedObjects.filter(
      (obj) => obj.status === "plan_vs_real_dev",
    ).length;
    const real_vs_plan_dev = detectedObjects.filter(
      (obj) => obj.status === "real_vs_plan_dev",
    ).length;
    const occluded = detectedObjects.filter(
      (obj) => obj.status === "occluded",
    ).length;

    return {
      total,
      installed,
      uninstalled,
      plan_vs_real_dev,
      real_vs_plan_dev,
      occluded,
    };
  };

  const stats = getObjectStats();

  const handleStatusChange = (status) => {
    handleClearInProgressDraw();
    setShouldFlash(true);

    let statuses;
    if (status === null) {
      statuses =
        selectedStatuses.length === Object.keys(STATUSES).length
          ? []
          : Object.keys(STATUSES);
    } else {
      statuses = selectedStatuses.includes(status)
        ? selectedStatuses.filter((s) => s !== status)
        : [...selectedStatuses, status];
    }
    setSelectedStatuses(statuses);
    setSelectedTypes([]);
  };

  const groupedObjects = useMemo(() => {
    return detectedObjects
      .filter((obj) => selectedStatuses.includes(obj.status))
      .reduce((acc, obj) => {
        if (!acc[obj.group_id]) acc[obj.group_id] = [];
        acc[obj.group_id].push(obj);
        return acc;
      }, {});
  }, [detectedObjects, selectedStatuses]);

  useEffect(() => {
    setSelectedTypes(
      Object.keys(groupedObjects).map((type) => type.toString()),
    );
  }, [groupedObjects]);

  useEffect(() => {
    setSelectedStatuses(Object.keys(STATUSES));
  }, [detectedObjects]);

  const handleTypeChange = (type) => {
    handleClearInProgressDraw();
    setShouldFlash(true);

    let types;
    if (type === null) {
      types =
        selectedTypes.length === Object.keys(groupedObjects).length
          ? []
          : Object.keys(groupedObjects);
    } else {
      types = selectedTypes.includes(type)
        ? selectedTypes.filter((t) => t !== type)
        : [...selectedTypes, type];
    }
    setSelectedTypes(types);
  };

  const filteredObjectIdsRef = useRef(filteredObjectIds);
  filteredObjectIdsRef.current = filteredObjectIds;

  const handleSetObjectIds = (ids: string[]) => {
    if (ids.length === 0 && filteredObjectIdsRef.current.length === 0) {
      return;
    }

    handleClearInProgressDraw();
    setShouldFlash(true);
    setSelectedObjects([]);
    setSelectedTypes(Object.keys(groupedObjects));
    setSelectedStatuses(Object.keys(STATUSES));
    setFilteredObjectIds(ids);
  };

  // ChatBot state and handler
  const [messages, setMessages] = useState<Message[]>([
    {
      sender: "bot",
      text: t("chatbot.welcome_message"),
    },
  ]);

  const setMessage = (message: Message) => {
    // If it's a bot message, check for object IDs
    if (message.sender === "bot" && !message.isError) {
      console.log("🔍 Received message with objectIds:", message.objectIds);

      // New API structure: objectIds are passed directly in the message object
      const objectIds = message.objectIds || [];

      if (objectIds.length > 0) {
        // Create a button to clear the filtered objects
        const clearObjectsMessage: Message = {
          sender: "bot",
          text: (
            <button
              type="button"
              onClick={() => handleSetObjectIds([])}
              className="px-3 py-1 rounded-md bg-blue-500 text-white hover:bg-blue-600 active:scale-95 transition transform duration-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Clear filtered objects ({objectIds.length})
            </button>
          ),
        };

        setMessages((prev) => [...prev, message, clearObjectsMessage]);
        handleSetObjectIds(objectIds);

        console.log(`✅ Highlighting ${objectIds.length} objects on plan`);
      } else {
        setMessages((prev) => [...prev, message]);
      }
    } else {
      // User messages or error messages - just add them
      setMessages((prev) => [...prev, message]);
    }
  };

  const isLoading = loading || !planImageLoaded;

  return (
    <div className="relative h-full w-full flex">
      {/* <div className="flex flex-1 overflow-hidden gap-4 relative"> */}
      {/* Plan View */}
      <div className="flex-1 flex flex-col min-w-0 p-4">
        {!isViewMode && (
          <div className="pb-4">
            <BackButton
              label={sectionData.name}
              to={`/project/${sectionData.project._id}/sections/${sectionData._id}`}
            />
          </div>
        )}
        <div
          className="flex-1 relative bg-white rounded-lg border overflow-hidden w-full h-full"
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseLeave={handleMouseLeave}
          onMouseMove={handleContainerMouseMove}
          style={{
            cursor: isHoveringObject
              ? "pointer"
              : isDragging || isTouching
                ? "grabbing"
                : "grab",
            touchAction: "none", // Prevent default touch behaviors
          }}
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p className="text-gray-600">
                  {t("progress_report.loading_objects")}
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center text-red-500 z-10">
              <p>{t("progress_report.loading_error", { error })}</p>
            </div>
          )}

          {/* Plan Image */}
          <div
            className="relative w-full h-full"
            style={{ overflow: "hidden" }}
          >
            {sectionData.plan?.previewUrl ? (
              <div
                className="relative w-full h-full"
                style={{
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomLevel})`,
                  transformOrigin: "top left",
                  willChange: "transform",
                }}
              >
                <img
                  ref={planImageRef}
                  src={sectionData.plan.previewUrl}
                  alt={t("progress_report.plan_alt", {
                    sectionName: sectionData.name,
                  })}
                  className={`max-w-none select-none ${planImageLoaded ? "" : "opacity-0"}`}
                  onLoad={() => setPlanImageLoaded(true)}
                  onError={() =>
                    setError(t("progress_report.image_load_error"))
                  }
                  draggable={false}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Settings size={48} className="mx-auto mb-2" />
                  <p>{t("progress_report.no_plan_available")}</p>
                </div>
              </div>
            )}
          </div>

          {/* Object Overlay Canvas - sized to container, not image */}
          <canvas
            ref={canvasRef}
            className="absolute top-0 left-0 pointer-events-none"
          />
        </div>
        <div className="flex justify-center items-center space-x-2 pt-4">
          <button
            onClick={() => handleZoom("out")}
            className="p-2 bg-white text-gray-600 rounded border hover:bg-gray-200 disabled:opacity-50"
            title={t("progress_report.zoom_out")}
            disabled={zoomLevel <= ZOOM_CONFIG.MIN}
          >
            <ZoomOut size={16} />
          </button>

          <button
            onClick={() => handleZoom("in")}
            className="p-2 bg-white text-gray-600 rounded border hover:bg-gray-200 disabled:opacity-50"
            title={t("progress_report.zoom_in")}
            disabled={zoomLevel >= ZOOM_CONFIG.MAX}
          >
            <ZoomIn size={16} />
          </button>
        </div>
      </div>

      {/* Side Panel */}
      {!isViewMode && (
        <div className="flex">
          <div className="w-80 border-l border-r bg-white flex flex-col overflow-y-auto">
            {/* Object Details */}
            {selectedObject ? (
              <div className="p-4 border-b">
                <h4 className="font-medium text-gray-900 mb-2">
                  {t("progress_report.object_details")}
                </h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">
                      {t("progress_report.group")}:
                    </span>{" "}
                    {selectedObject.group_id}
                  </div>
                  <div>
                    <span className="font-medium">
                      {t("progress_report.element_status")}:
                    </span>
                    <span
                      className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                        selectedObject.status === "installed"
                          ? "bg-green-100 text-green-800"
                          : selectedObject.status === "uninstalled"
                            ? "bg-orange-100 text-orange-800"
                            : selectedObject.status === "real_vs_plan_dev"
                              ? "bg-red-100 text-red-800"
                              : selectedObject.status === "occluded"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {selectedObject.status}
                    </span>
                  </div>
                  {selectedObject.cableStatus && (
                    <div>
                      <span className="font-medium">
                        {t("progress_report.cable_status")}:
                      </span>
                      <span
                        className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                          selectedObject.cableStatus === "installed"
                            ? "bg-green-100 text-green-800"
                            : "bg-orange-100 text-orange-800"
                        }`}
                      >
                        {selectedObject.cableStatus}
                      </span>
                    </div>
                  )}
                  {/* <div>
                    <span className="font-medium">
                      {t("progress_report.position")}:
                    </span>{" "}
                    ({selectedObject.position.x}, {selectedObject.position.y})
                  </div> */}
                  <div>
                    <span className="font-medium">
                      {t("progress_report.grid_position")}:
                    </span>{" "}
                    [{selectedObject.chessboard_id}]
                  </div>
                  <div>
                    <span className="font-medium">
                      {t("progress_report.detection_date")}:
                    </span>{" "}
                    {/* we don't even have this detectionDate, it is just undefined
                    and it renders the current date */}
                    {dayjs
                      .utc(selectedObject.detectionDate)
                      .format("YYYY-MM-DD HH:mm")}
                  </div>
                  <div>
                    <span className="font-medium">
                      {t("progress_report.description")}:
                    </span>{" "}
                    {selectedObject.description}
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  <ShortestPathPanel
                    cablePathState={cablePathState}
                    isCalculatingShortestPath={isCalculatingShortestPath}
                    isObjectSelected={!isLoading && Boolean(selectedObject)}
                    onFindShortestPath={findShortestPath}
                    onClearRoute={resetShortestPath}
                  />
                  <div className="mb-4">
                    <button
                      onClick={toggleCablePaths}
                      disabled={loadingPaths}
                      className={clsx(
                        "w-full px-3 py-2 text-sm text-white rounded",
                        "flex items-center justify-center gap-2 transition-colors",
                        "disabled:opacity-50 disabled:cursor-not-allowed",
                        showCablePaths
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-blue-600 hover:bg-blue-700",
                      )}
                    >
                      {loadingPaths ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          {t("progress_report.loading_cables")}
                        </>
                      ) : showCablePaths ? (
                        t("progress_report.hideCableTraces")
                      ) : (
                        t("progress_report.showCableTraces")
                      )}
                    </button>
                  </div>
                  {selectedObject &&
                    selectedObject.documentations &&
                    selectedObject.documentations.length > 0 &&
                    !isViewMode && (
                      <button
                        onClick={() => {
                          const lastDocumentation =
                            selectedObject.documentations[
                              selectedObject.documentations.length - 1
                            ];
                          const lastReferenceFrame =
                            lastDocumentation.referenceFrames[
                              lastDocumentation.referenceFrames.length - 1
                            ];
                          handleOpenDocumentation(
                            lastReferenceFrame + ".jpg",
                            selectedObject._id,
                            lastDocumentation.documentationId,
                          );
                        }}
                        className="w-full px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center justify-center"
                      >
                        <svg
                          className="w-4 h-4 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                        {t("progress_report.see_in_documentation")}
                      </button>
                    )}
                </div>
              </div>
            ) : (
              <div className="p-4 border-b text-center text-gray-500 flex flex-col gap-3">
                <div className="flex justify-between items-center">
                  <p className="text-sm">
                    {t("progress_report.click_object_for_details")}
                  </p>
                  <Settings size={20} />
                </div>
                {projectId === "49" && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() =>
                      window.open(
                        "/all-circuits/project-49-cabel-trays.html",
                        "_blank",
                      )
                    }
                  >
                    {t("progress_report.show_cable_trays")}
                  </Button>
                )}
              </div>
            )}
            {/* Objects List */}
            <div className="flex-1 overflow-y-auto min-h-96">
              {!isLoading ? (
                <div className="p-4">
                  <div className="flex flex-col items-center space-x-4 text-md text-gray-600 mt-1">
                    {[
                      {
                        key: null,
                        label: t("progress_report.total_objects"),
                        colorClass:
                          "text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 shadow-sm mb-2",
                        stat: stats.total,
                      },
                      {
                        key: STATUSES.installed,
                        label: t("progress_report.installed"),
                        colorClass:
                          "text-green-600 bg-green-50 hover:bg-green-100",
                        stat: stats.installed,
                      },
                      {
                        key: STATUSES.uninstalled,
                        label: t("progress_report.not_installed"),
                        colorClass:
                          "text-orange-600 bg-orange-50 hover:bg-orange-100",
                        stat: stats.uninstalled,
                      },
                      {
                        key: STATUSES.real_vs_plan_dev,
                        label: t("progress_report.real_vs_plan_dev"),
                        colorClass: "text-red-600 bg-red-50 hover:bg-red-100",
                        stat: stats.real_vs_plan_dev,
                      },
                      {
                        key: STATUSES.occluded,
                        label: t("progress_report.occluded"),
                        colorClass:
                          "text-amber-600 bg-amber-50 hover:bg-amber-100",
                        stat: stats.occluded,
                      },
                    ].map(({ key, label, colorClass, stat }) => (
                      <label
                        key={label}
                        className={`flex items-center space-x-1 cursor-pointer select-none ${colorClass} px-2 py-1 rounded transition-colors w-full`}
                      >
                        <input
                          type="checkbox"
                          checked={
                            key === null
                              ? selectedStatuses.length ===
                                Object.keys(STATUSES).length
                              : selectedStatuses.includes(key)
                          }
                          onChange={() => handleStatusChange(key)}
                          className="form-checkbox accent-blue-600"
                        />
                        <span>
                          {label}: ({stat})
                        </span>
                      </label>
                    ))}
                  </div>
                  {Object.keys(groupedObjects).length > 0 && (
                    <>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 mt-2">
                        {t("progress_report.detected_object_groups")}
                      </h4>
                      <div className="flex flex-col items-center space-x-4 text-sm text-gray-600 mt-1">
                        <label
                          className={`flex items-center space-x-1 cursor-pointer select-nonetext-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 shadow-sm mb-2 px-2 py-1 rounded transition-colors w-full`}
                        >
                          <input
                            type="checkbox"
                            checked={
                              selectedTypes.length ===
                              Object.keys(groupedObjects).length
                            }
                            onChange={() => handleTypeChange(null)}
                            className="form-checkbox accent-blue-600"
                          />
                          <span>
                            {t("progress_report.all_types")}: (
                            {Object.values(groupedObjects).reduce(
                              (sum, arr) => sum + arr.length,
                              0,
                            )}
                            )
                          </span>
                        </label>
                        {Object.keys(groupedObjects)
                          .map((type) => ({
                            key: type,
                            label: type,
                            stat: groupedObjects[type].length,
                          }))
                          .sort((a, b) => b.stat - a.stat)
                          .map(({ key, label, stat }) => (
                            <label
                              key={label}
                              className={`flex items-center space-x-1 cursor-pointer select-none text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors w-full`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedTypes.includes(key)}
                                onChange={() => handleTypeChange(key)}
                                className="form-checkbox accent-blue-600"
                              />
                              <span>
                                {label}: ({stat})
                              </span>
                            </label>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <ChatBot
            isInitialOpen={true}
            messages={messages}
            setMessage={setMessage}
            projectId={projectId}
            sectionId={sectionData?._id}
            isDisabled={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default ProgressReportViewer;
